import { supabase } from '../lib/supabase';
import type { RadarRole } from '../types/app';
import { getAssessmentClassIds, loadAssessments, loadTeacherClassIds, subjectLabel, type Assessment } from './assessments';

export type LaunchStudent = {
  id:string;
  school_id:string;
  class_id:string;
  student_code:string|null;
  name:string;
  status:string;
  classes?:{id?:string;name:string;grade:number;shift:string|null}|null;
};

export type LaunchResult = {
  launch_id:string|null;
  school_id:string|null;
  class_id:string|null;
  student_id:string|null;
  assessment_id:string|null;
  student_name:string|null;
  student_code:string|null;
  class_name:string|null;
  grade:number|null;
  assessment_name:string|null;
  subject:string|null;
  item_count:number|null;
  correct_answers:number|null;
  wrong_answers:number|null;
  percentage:number|null;
  submitted_at:string|null;
};

function needClient(){if(!supabase)throw new Error('Supabase não configurado.');return supabase;}
function normClassName(v:string){return String(v||'').normalize('NFD').replace(/[\u0300-\u036f]/g,'').toUpperCase().replace(/[^A-Z0-9]+/g,'');}

export async function loadLaunchAssessments(schoolId:string,role:RadarRole){
  return loadAssessments(schoolId,role);
}

export async function loadLaunchResults(schoolId:string,role:RadarRole){
  const sb=needClient();
  let q=sb.from('student_assessment_results').select('*').eq('school_id',schoolId).order('submitted_at',{ascending:false});
  if(role==='teacher'){
    const ids=await loadTeacherClassIds();
    if(!ids.length)return [] as LaunchResult[];
    q=q.in('class_id',ids);
  }
  const {data,error}=await q;
  if(error)throw error;
  const map=new Map<string,LaunchResult>();
  for(const r of data||[]){
    const key=`${r.assessment_id}|${r.student_id}`;
    if(!map.has(key))map.set(key,r as LaunchResult);
  }
  return [...map.values()];
}

export function countAssessmentLaunches(rows:LaunchResult[],assessmentId:string){
  return rows.filter(r=>r.assessment_id===assessmentId).length;
}

export async function studentsForAssessment(assessment:Assessment,schoolId:string,role:RadarRole){
  const sb=needClient();
  const linkedIds=await getAssessmentClassIds(assessment.id);
  const teacherIds=role==='teacher'?await loadTeacherClassIds():[];
  const allowedLinkedIds=role==='teacher'?linkedIds.filter(id=>teacherIds.includes(id)):linkedIds;
  if(!allowedLinkedIds.length)return [] as LaunchStudent[];

  // Regra legada v15.9.8: a turma vinculada ao simulado é a âncora.
  // Alguns estudantes importados podem manter class_id correto e school_id divergente.
  const {data:classData,error:classError}=await sb.from('classes').select('id,name,grade,shift,school_id').eq('school_id',schoolId);
  if(classError)throw classError;
  const classCatalog=(classData||[]) as {id:string;name:string;grade:number;shift:string|null;school_id:string}[];
  const linkedMeta=classCatalog.filter(c=>allowedLinkedIds.includes(c.id));
  const signatures=new Set(linkedMeta.map(c=>`${Number(c.grade)||0}|${normClassName(c.name)}`));
  let equivalentIds=classCatalog.filter(c=>allowedLinkedIds.includes(c.id)||signatures.has(`${Number(c.grade)||0}|${normClassName(c.name)}`)).map(c=>c.id);
  equivalentIds=[...new Set([...allowedLinkedIds,...equivalentIds])];

  if(role==='teacher'){
    const teacherMeta=classCatalog.filter(c=>teacherIds.includes(c.id));
    const teacherSignatures=new Set(teacherMeta.map(c=>`${Number(c.grade)||0}|${normClassName(c.name)}`));
    equivalentIds=equivalentIds.filter(id=>{
      const c=classCatalog.find(x=>x.id===id);
      return !!(c&&teacherSignatures.has(`${Number(c.grade)||0}|${normClassName(c.name)}`));
    });
  }
  if(!equivalentIds.length)return [] as LaunchStudent[];

  const {data,error}=await sb.from('students').select('id,school_id,class_id,student_code,name,status,classes(id,name,grade,shift)').in('class_id',equivalentIds).order('name');
  if(error)throw error;
  return (data||[]).filter((s:any)=>String(s.status||'').toUpperCase()!=='TRANSFERRED').map((s:any)=>({...s,classes:Array.isArray(s.classes)?s.classes[0]:s.classes})) as LaunchStudent[];
}

export async function getSavedAnswers(assessmentId:string,studentId:string){
  const sb=needClient();
  const {data:launch,error:launchError}=await sb.from('launches').select('id').eq('assessment_id',assessmentId).eq('student_id',studentId).order('submitted_at',{ascending:false}).limit(1).maybeSingle();
  if(launchError)throw launchError;
  if(!launch?.id)return [] as {item_number:number;answer:string|null}[];
  const {data,error}=await sb.from('student_answers').select('answer,assessment_items!inner(item_number)').eq('launch_id',launch.id).order('assessment_items(item_number)');
  if(error)throw error;
  return (data||[]).map((x:any)=>({item_number:Number(Array.isArray(x.assessment_items)?x.assessment_items[0]?.item_number:x.assessment_items?.item_number),answer:x.answer||null})).filter(x=>x.item_number>0);
}

async function submitAnswersWithSource(assessmentId:string,studentId:string,answers:string[],source:string){
  const sb=needClient();
  const payload=answers.map((answer,index)=>({questao:index+1,resposta:String(answer||'').toUpperCase()}));
  let result=await sb.rpc('submit_student_answers_replace',{p_assessment_id:assessmentId,p_student_id:studentId,p_answers:payload,p_source:source});
  if(result.error){
    result=await sb.rpc('submit_student_answers',{p_assessment_id:assessmentId,p_student_id:studentId,p_answers:payload,p_source:source});
  }
  if(result.error)throw result.error;
  return result.data as any;
}

export async function submitManualAnswers(assessmentId:string,studentId:string,answers:string[]){
  return submitAnswersWithSource(assessmentId,studentId,answers,'MANUAL');
}

export async function submitOmrAnswers(assessmentId:string,studentId:string,answers:string[]){
  return submitAnswersWithSource(assessmentId,studentId,answers,'CAMERA');
}

export function assessmentSummary(a:Assessment){return `${a.grade}º ano · ${subjectLabel(a.subject)} · ${a.item_count} questões`;}
